import type { ProjectBrief, Task } from '@dyspatch/core';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ExternalLink, FolderGit2 } from 'lucide-react';
import { Age, Empty, prNumber, repoColor, TaskKey } from '@/components/common/bits';
import { SortMenu } from '@/components/common/SortMenu';
import { StateGroup } from '@/components/common/StateGroup';
import { BriefIcon, TaskIcon, taskTone } from '@/components/common/StatusIcon';
import { Page, ViewHeader } from '@/components/layout/Page';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { PausedChip } from '@/features/actions/PauseActions';
import { RecentActivity } from '@/features/activity/RecentActivity';
import { ProjectHeader } from '@/features/projects/ProjectHeader';
import { ReviewMetricsStrip } from '@/features/review/ReviewMetrics';
import { useCollapsedGroups } from '@/hooks/use-collapsed-groups';
import { useSort } from '@/hooks/use-sort';
import { feedItemsFromEvents } from '@/lib/activity';
import { api } from '@/lib/api';
import { briefGroups } from '@/lib/briefs';
import { labelForKind } from '@/lib/event-kinds';
import { duration, minutesSince } from '@/lib/format';
import { hubKeys } from '@/lib/query';
import { applySort } from '@/lib/sorting';
import { TASK_STATUS_FILTERS, type TaskStatusFilter } from '@/lib/task-groups';
import { cn } from '@/lib/utils';
import { JOB_SORT_KEYS, jobSortOptions, STEP_SORT_KEYS, stepSortOptions } from '@/lib/work-sort';

/** How many rows a collapsed-by-default group shows before "see all" takes over (issue #68). */
const GROUP_PREVIEW_LIMIT = 5;

/** The task states this page groups by, `gate` left out: "Waiting on a person"
 * above already covers those tasks in full, so grouping them a second time
 * here would say the same thing twice. */
const PROJECT_TASK_GROUPS = TASK_STATUS_FILTERS.filter(
  (filter) => filter.value !== 'all' && filter.value !== 'gate',
);

/** Live groups stay open the first time a viewer sees this page; history starts collapsed. */
const DEFAULT_OPEN_TASK_GROUPS = ['running'];
const DEFAULT_OPEN_JOB_GROUPS = ['gate', 'working'];

interface PreviewGroup<T> {
  key: string;
  label: string;
  empty: string;
  /** The whole repo's count in this state, not merely how many rows are shown. */
  total: number;
  rows: T[];
}

/**
 * `matching` is sorted and cut to `GROUP_PREVIEW_LIMIT` for display; `total`
 * is the whole repo's count in the state, which is what a collapsed group
 * has to show to be worth anything (issue #68).
 */
function previewGroup<T>(
  key: string,
  label: string,
  empty: string,
  matching: readonly T[],
  total: number,
): PreviewGroup<T> {
  return { key, label, empty, total, rows: matching.slice(0, GROUP_PREVIEW_LIMIT) };
}

function projectTaskGroups(
  tasks: readonly Task[],
  byStatus: Record<string, number>,
  sortState: Parameters<typeof applySort>[2],
): PreviewGroup<Task>[] {
  return PROJECT_TASK_GROUPS.map((filter) => {
    const statuses = filter.statuses as readonly string[];
    const matching = applySort(
      tasks.filter((task) => statuses.includes(task.status)),
      stepSortOptions(),
      sortState,
      (task) => task.id,
    );
    const total = statuses.reduce((sum, status) => sum + (byStatus[status] ?? 0), 0);
    return previewGroup(
      filter.value,
      filter.label,
      `Nothing ${filter.label.toLowerCase()}.`,
      matching,
      total,
    );
  });
}

function projectJobGroups(
  repoBriefs: readonly ProjectBrief[],
  repoTasks: readonly Task[],
  sortState: Parameters<typeof applySort>[2],
): PreviewGroup<ProjectBrief>[] {
  return briefGroups(repoBriefs, repoTasks).map((group) =>
    previewGroup(
      group.key,
      group.label,
      group.empty,
      applySort(group.briefs, jobSortOptions(), sortState, (brief) => brief.id),
      group.briefs.length,
    ),
  );
}

/**
 * One repo, end to end.
 *
 * Two things were wrong with this page and the second one mattered.
 *
 * It opened with twelve numbers: a six-cell rollup strip stacked on a six-cell
 * review strip, every figure the same size, which is the arrangement the home
 * page was rebuilt to get rid of. They are now two regions with headings, because
 * they answer two different questions -- what is in this repo right now, and how
 * fast review moves in it -- and a reader who wants the second one should have to
 * look for it rather than wade through it.
 *
 * And its Activity panel printed the journal raw, which is the leak the home page
 * was fixed for: forty lines of the dispatcher re-holding the same three tasks,
 * carrying colleagues' email addresses and how much of their Claude window was
 * left, on a page any member can open. Fixing that in one place and leaving it
 * here fixed nothing. It reads the same milestone strip now.
 *
 * The pull requests in review are rows that link rather than rows with five
 * verbs each. The gates are decided on the dashboard, where all of them are; drawing
 * the same five buttons a second time here is how a page ends up with twenty
 * controls and no subject.
 */
function lastActivity(at: string | null): string {
  if (!at) return 'quiet';
  const minutes = minutesSince(at);
  return minutes < 1 ? 'active just now' : `last activity ${duration(minutes)} ago`;
}

function Stat({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string | number;
  note?: string;
  tone?: string;
}) {
  return (
    <div className="min-w-0 px-gutter py-4">
      <div className="dy-eyebrow truncate">{label}</div>
      <div
        className="dy-figure mt-1.5 text-xl leading-none"
        style={tone ? { color: tone } : undefined}
      >
        {value}
      </div>
      <div className="text-fg-3 mt-1.5 truncate text-2xs">{note ?? ' '}</div>
    </div>
  );
}

export function ProjectPage() {
  const { repo } = useParams({ from: '/project/$repo' });
  const project = useQuery({
    queryKey: hubKeys.project(repo),
    queryFn: () => api.project(repo),
    refetchInterval: 30_000,
  });

  // Read before the early returns below, since a hook cannot be called only on
  // the render that has data. Each list gets its own pair of search keys, or
  // the two controls would fight over one `sort`/`dir` (see `useSort`).
  const stepsSorting = useSort(stepSortOptions(), (task) => task.id, STEP_SORT_KEYS);
  const jobsSorting = useSort(jobSortOptions(), (brief) => brief.id, JOB_SORT_KEYS);
  // Same reason: every page's groups collapse the same way regardless of
  // which repo you are looking at, so the choice is read once here rather
  // than re-derived per group.
  const taskGroupsOpen = useCollapsedGroups(
    'project.tasks',
    PROJECT_TASK_GROUPS.map((filter) => filter.value),
    DEFAULT_OPEN_TASK_GROUPS,
  );
  const jobGroupsOpen = useCollapsedGroups(
    'project.jobs',
    ['gate', 'working', 'waiting', 'closed'],
    DEFAULT_OPEN_JOB_GROUPS,
  );

  if (project.isPending) {
    return (
      <>
        <ViewHeader title={repo} icon={FolderGit2} tone={repoColor(repo)} width="1100px" />
        <Page className="max-w-[1100px]">
          <Surface>
            <Empty>Loading {repo}…</Empty>
          </Surface>
        </Page>
      </>
    );
  }
  if (project.isError) {
    return (
      <>
        <ViewHeader title={repo} icon={FolderGit2} tone={repoColor(repo)} width="1100px" />
        <Page className="max-w-[1100px]">
          <Surface>
            <Empty>
              {project.error instanceof Error ? project.error.message : `no such project: ${repo}`}
            </Empty>
          </Surface>
        </Page>
      </>
    );
  }

  const {
    rollup,
    inReview,
    tasks,
    briefs,
    review,
    events,
    description,
    defaultBranch,
    githubUrl,
    contributors,
  } = project.data;
  const byStatus = Object.entries(rollup.tasks.byStatus).sort((a, b) => b[1] - a[1]);

  // Grouped by state and bounded to the recent few, rather than every task
  // or job the repo has ever had in one flat, ever-growing list (issue #68).
  const taskGroups = projectTaskGroups(tasks, rollup.tasks.byStatus, stepsSorting.state);
  const jobGroups = projectJobGroups(briefs, tasks, jobsSorting.state);

  return (
    <>
      <ViewHeader
        title={repo}
        icon={FolderGit2}
        tone={repoColor(repo)}
        sub={lastActivity(rollup.lastActivityAt)}
        width="1100px"
      />
      <Page className="max-w-[1100px]">
        <div className="dy-stack">
          <ProjectHeader
            repo={repo}
            description={description}
            defaultBranch={defaultBranch}
            githubUrl={githubUrl}
            contributors={contributors}
          />

          <Section>
            <SectionHead title="Right now" meta={`${rollup.tasks.total} tasks ever`} />
            <Surface>
              <div className="divide-line grid grid-cols-2 divide-x divide-y sm:grid-cols-3 xl:grid-cols-5 xl:divide-y-0">
                <Stat
                  label="Waiting on a person"
                  value={rollup.inReview}
                  note={
                    rollup.oldestReviewMinutes
                      ? `oldest ${duration(rollup.oldestReviewMinutes)}`
                      : 'nothing waiting'
                  }
                  tone={rollup.inReview ? 'var(--dy-gate)' : undefined}
                />
                <Stat
                  label="Running"
                  value={rollup.tasks.inFlight}
                  note={`${rollup.activeWorkers} device${rollup.activeWorkers === 1 ? '' : 's'} online`}
                />
                <Stat label="Queued" value={rollup.tasks.queued} />
                <Stat label="Open jobs" value={rollup.openBriefs} />
                <Stat
                  label="Merged in 7 days"
                  value={rollup.mergedLast7d}
                  note={
                    rollup.medianReviewMinutes
                      ? `median review ${duration(rollup.medianReviewMinutes)}`
                      : `retry rate ${Math.round(rollup.retryRate * 100)}%`
                  }
                  tone={rollup.mergedLast7d ? 'var(--dy-ok)' : undefined}
                />
              </div>
            </Surface>
          </Section>

          <div className="grid gap-[var(--dy-gap)] xl:grid-cols-[minmax(0,1fr)_340px]">
            <div className="dy-stack min-w-0">
              <Section>
                <SectionHead
                  title="Waiting on a person"
                  hue={inReview.length ? 'var(--dy-gate)' : undefined}
                  meta={inReview.length ? 'decide these on the dashboard' : undefined}
                  actions={
                    inReview.length ? (
                      <Link
                        to="/"
                        className="text-fg-3 hover:text-foreground text-xs transition-colors duration-100"
                      >
                        Go there →
                      </Link>
                    ) : null
                  }
                />
                <Surface>
                  {inReview.length ? (
                    <ul className="divide-line divide-y">
                      {inReview.map((entry) => (
                        <li
                          key={entry.id}
                          className="hover:bg-raised/60 flex items-start gap-3 px-gutter py-3 transition-colors duration-100"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex min-w-0 items-baseline gap-2.5">
                              <TaskKey briefId={entry.briefId}>{entry.key}</TaskKey>
                              <Link
                                to="/tasks/$taskId"
                                params={{ taskId: entry.id }}
                                className="hover:text-gate min-w-0 truncate text-base transition-colors duration-100"
                              >
                                {entry.title}
                              </Link>
                            </div>
                            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
                              <span className="text-fg-3 min-w-0 truncate text-xs">
                                {entry.briefTitle}
                              </span>
                              {entry.prUrl ? (
                                <a
                                  href={entry.prUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-fg-3 hover:text-gate inline-flex shrink-0 items-center gap-1 text-xs transition-colors duration-100"
                                >
                                  PR{prNumber(entry.prUrl)}
                                  <ExternalLink className="size-3" aria-hidden />
                                </a>
                              ) : null}
                            </div>
                          </div>
                          <span className="mt-0.5 shrink-0">
                            <Age minutes={entry.waitingMinutes} hot />
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-fg-3 px-gutter py-8 text-center text-sm">
                      Nothing in {repo} is waiting on a person. Pull requests this repo's agents
                      open queue up here.
                    </p>
                  )}
                </Surface>
              </Section>

              <Section>
                <SectionHead
                  title="Tasks"
                  meta={`${rollup.tasks.total} in total`}
                  actions={
                    <SortMenu
                      options={stepsSorting.options}
                      state={stepsSorting.state}
                      onChange={stepsSorting.choose}
                    />
                  }
                />
                <Surface>
                  {tasks.length ? (
                    <div className="divide-line divide-y">
                      {taskGroups.map((group) => (
                        <StateGroup
                          key={group.key}
                          label={group.label}
                          count={group.total}
                          open={!taskGroupsOpen.isCollapsed(group.key)}
                          onToggle={() => taskGroupsOpen.toggle(group.key)}
                        >
                          {group.rows.length ? (
                            <ul className="divide-line divide-y border-t">
                              {group.rows.map((task) => (
                                <li
                                  key={task.id}
                                  className="flex items-start gap-3 px-gutter py-2.5"
                                >
                                  <TaskIcon status={task.status} className="mt-0.5" />
                                  <TaskKey briefId={task.briefId}>{task.key}</TaskKey>
                                  <Link
                                    to="/tasks/$taskId"
                                    params={{ taskId: task.id }}
                                    className="hover:text-gate min-w-0 flex-1 truncate text-base transition-colors duration-100"
                                  >
                                    {task.title}
                                  </Link>
                                  {task.pausedAt ? <PausedChip className="self-center" /> : null}
                                  <span
                                    className={cn(
                                      'hidden w-20 shrink-0 truncate text-xs font-medium sm:block',
                                      taskTone(task.status),
                                    )}
                                  >
                                    {task.status}
                                  </span>
                                  <span className="mt-0.5 w-12 shrink-0 text-right whitespace-nowrap">
                                    <Age minutes={minutesSince(task.updatedAt)} />
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-fg-3 border-t px-gutter py-4 text-center text-xs">
                              {group.empty}
                            </p>
                          )}
                          {group.total > group.rows.length ? (
                            <div className="border-t px-gutter py-2 text-right">
                              <Link
                                to="/jobs"
                                search={{
                                  board: true,
                                  repo,
                                  status: group.key as TaskStatusFilter,
                                }}
                                className="text-fg-3 hover:text-foreground text-xs transition-colors duration-100"
                              >
                                See all {group.total} →
                              </Link>
                            </div>
                          ) : null}
                        </StateGroup>
                      ))}
                    </div>
                  ) : (
                    <Empty>No tasks yet.</Empty>
                  )}
                </Surface>
              </Section>

              <Section>
                <SectionHead title="How fast review moves" meta="in this repo" />
                <ReviewMetricsStrip metrics={review} />
              </Section>
            </div>

            <div className="dy-stack min-w-0">
              <Section>
                <SectionHead title="By state" />
                <Surface>
                  {byStatus.length ? (
                    <ul className="divide-line divide-y">
                      {byStatus.map(([status, count]) => (
                        <li
                          key={status}
                          className="flex items-center gap-2 px-gutter py-2.5 text-sm"
                        >
                          <span className="text-fg-2">{status}</span>
                          <span className="tnum text-fg-3 ml-auto">{count}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-fg-3 px-gutter py-5 text-center text-sm">Nothing yet.</p>
                  )}
                </Surface>
              </Section>

              <Section>
                <SectionHead
                  title="Jobs naming this repo"
                  actions={
                    <SortMenu
                      options={jobsSorting.options}
                      state={jobsSorting.state}
                      onChange={jobsSorting.choose}
                    />
                  }
                />
                <Surface>
                  {briefs.length ? (
                    <div className="divide-line divide-y">
                      {jobGroups.map((group) => (
                        <StateGroup
                          key={group.key}
                          label={group.label}
                          count={group.total}
                          open={!jobGroupsOpen.isCollapsed(group.key)}
                          onToggle={() => jobGroupsOpen.toggle(group.key)}
                        >
                          {group.rows.length ? (
                            <ul className="divide-line divide-y border-t">
                              {group.rows.map((brief) => (
                                <li
                                  key={brief.id}
                                  className="flex items-start gap-3 px-gutter py-2.5"
                                >
                                  <BriefIcon status={brief.status} className="mt-0.5" />
                                  <Link
                                    to="/jobs/$briefId"
                                    params={{ briefId: brief.id }}
                                    className="hover:text-gate min-w-0 flex-1 truncate text-base transition-colors duration-100"
                                  >
                                    {brief.title}
                                  </Link>
                                  {/* Every job in this list names this repo, so a repo chip
                                      said nothing. Its state does, and "+1 repo" still says
                                      "and somewhere else too". */}
                                  <span className="text-fg-3 shrink-0 text-xs">
                                    {labelForKind(brief.status)}
                                    {brief.repos.length > 1
                                      ? ` · +${brief.repos.length - 1} repo`
                                      : ''}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-fg-3 border-t px-gutter py-4 text-center text-xs">
                              {group.empty}
                            </p>
                          )}
                          {group.total > group.rows.length ? (
                            <div className="border-t px-gutter py-2 text-right">
                              <Link
                                to="/jobs"
                                search={{ repo }}
                                className="text-fg-3 hover:text-foreground text-xs transition-colors duration-100"
                              >
                                See all {group.total} →
                              </Link>
                            </div>
                          ) : null}
                        </StateGroup>
                      ))}
                    </div>
                  ) : (
                    <Empty>No job names this repo.</Empty>
                  )}
                </Surface>
              </Section>

              {/* The same milestone strip the home page uses, over this repo's own
                  events. It used to be `EventLog` over the raw journal, which is
                  the leak `lib/activity.ts` exists to close. */}
              <RecentActivity items={feedItemsFromEvents(events)} limit={10} meta={`in ${repo}`} />
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}
